import React, { useCallback, useEffect, useState } from "react";
import {
  adminGetMobileNumberRequests,
  adminUpdateMobileNumberRequest,
} from "../../api/mobileNumberRequestApi";
import {
  adminGetRules,
  adminSaveRule,
  adminDeleteRule,
  adminGetNumerologyReports,
} from "../../api/mobileNumerologyApi";
import "./AdminConsultations.css"; // shared ac-* admin form/card styles
import "./AdminMobileNumerology.css";

const TYPES = [
  ["LUCKY_MATRIX", "Lucky totals (BN/DN)"],
  ["COMPOUND", "Mobile total compounds"],
  ["PAIR", "Pair meanings"],
  ["DOUBLE", "Same-digit pairs (11, 22...)"],
  ["ZERO_PAIR", "Zero placement (10, 20...)"],
  ["DIGIT_REPEAT", "Digit repeated 4+ times"],
  ["GRID_CONJUNCTION", "Grid: conjunctions"],
  ["GRID_TRIANGLE", "Grid: triangles"],
  ["GRID_PLANE", "Grid: planes"],
  ["GRID_DIAGONAL", "Grid: diagonals"],
  ["GRID_LSHAPE", "Grid: L-shapes"],
  ["PROFESSION", "Professions"],
  ["DIRECTION", "Charging direction"],
  ["PIN_ENEMY", "PIN enemy totals"],
  ["TOTAL_MEANING", "Total meanings"],
];
const RATINGS = ["excellent", "good", "mixed", "caution", "avoid"];

const dataHint = {
  LUCKY_MATRIX: '{ "lucky": [1,3,5,6], "unlucky": [8] }',
  COMPOUND: '{ "total": 1, "verdict": "good" }',
  PAIR: '{ "pairs": ["13","31"] }   (rating: good = green, mixed = yellow, avoid = red)',
  PROFESSION: '{ "tokens": ["p19","3","s319"] }   (7 = digit, p19 = pair, s319 = all digits)',
  DIRECTION: '{ "direction": "North" }',
  PIN_ENEMY: '{ "enemy": [4,9,2] }',
};
const gridHint = '{ "cells": [3,1] }   (2 digits for a conjunction, 3 for the rest)';

const emptyRule = (type) => ({
  id: null,
  type,
  key: "",
  title: "",
  rating: "good",
  summary: "",
  isActive: true,
  sortOrder: 0,
  data: "{}",
});

function Rules({ token }) {
  const [type, setType] = useState("PAIR");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const flash = (kind, text) => {
    setNotice({ type: kind, text });
    setTimeout(() => setNotice(null), 4500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminGetRules(type, token);
    if (r.ok && r.success) setList(r.data);
    else flash("error", r.message || "Unable to load rules");
    setLoading(false);
  }, [type, token]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const open = (rule) =>
    setForm(
      rule
        ? { ...rule, data: JSON.stringify(rule.data || {}, null, 2), summary: rule.summary || "" }
        : emptyRule(type)
    );

  const save = async (e) => {
    e.preventDefault();
    let data;
    try {
      data = JSON.parse(form.data || "{}");
    } catch {
      flash("error", "The data field is not valid JSON");
      return;
    }
    setSaving(true);
    const r = await adminSaveRule(
      form.id,
      {
        type: form.type,
        key: form.key,
        title: form.title,
        rating: form.rating,
        summary: form.summary,
        isActive: form.isActive,
        sortOrder: form.sortOrder,
        data,
      },
      token
    );
    setSaving(false);
    if (r.ok && r.success) {
      flash("success", form.id ? "Rule updated" : "Rule added");
      setForm(null);
      load();
    } else {
      flash("error", r.message || "Unable to save rule");
    }
  };

  const toggle = async (rule) => {
    const r = await adminSaveRule(rule.id, { isActive: !rule.isActive }, token);
    if (r.ok && r.success) load();
    else flash("error", r.message || "Unable to update");
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.title}"? The engine will stop using it.`)) return;
    const r = await adminDeleteRule(rule.id, token);
    if (r.ok && r.success) {
      flash("success", "Rule deleted");
      load();
    } else flash("error", r.message || "Unable to delete");
  };

  const hint = dataHint[form?.type] || (form?.type?.startsWith("GRID_") ? gridHint : "No extra data needed - use {}");

  return (
    <div className="ac-wrap">
      {notice && <div className={`feedback-alert ${notice.type}`}>{notice.text}</div>}

      {form ? (
        <form className="ac-form" onSubmit={save}>
          <h3>{form.id ? "Edit rule" : "Add rule"}</h3>
          <div className="ac-grid">
            <label>
              Type
              <select className="ac-input" value={form.type} disabled={Boolean(form.id)} onChange={(e) => set("type", e.target.value)}>
                {TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Key * <small>(e.g. 13-31, 37, 3-1)</small>
              <input className="ac-input" required maxLength={60} disabled={Boolean(form.id)} value={form.key} onChange={(e) => set("key", e.target.value)} />
            </label>
            <label>
              Rating
              <select className="ac-input" value={form.rating} onChange={(e) => set("rating", e.target.value)}>
                {RATINGS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <label>
              Sort order
              <input className="ac-input" type="number" value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} />
            </label>
          </div>
          <label>
            Title *
            <input className="ac-input" required maxLength={200} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </label>
          <label>
            Meaning shown to customers *
            <textarea className="ac-input" required rows={4} value={form.summary} onChange={(e) => set("summary", e.target.value)} />
          </label>
          <label>
            Data (JSON)
            <textarea className="ac-input ac-code" rows={4} value={form.data} onChange={(e) => set("data", e.target.value)} />
            <small className="mnA-hint">{hint}</small>
          </label>
          <label className="ac-check">
            <input type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} />
            Active (inactive rules are ignored by the engine)
          </label>
          <div className="ac-actions">
            <button type="button" className="ac-btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button type="submit" className="ac-btn ac-btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save rule"}
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="mnA-toolbar">
            <select className="ac-input" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <button type="button" className="ac-btn ac-btn-primary" onClick={() => open(null)}>
              + Add rule
            </button>
          </div>
          <p className="mnA-hint">Changes reach customers within about 5 minutes.</p>

          {loading ? (
            <p>Loading...</p>
          ) : list.length === 0 ? (
            <p>No rules of this type.</p>
          ) : (
            <div className="mnA-table">
              {list.map((rule) => (
                <div key={rule.id} className={`mnA-row ${rule.isActive ? "" : "is-off"}`}>
                  <div className="mnA-main">
                    <strong>
                      {rule.key} <span className={`mnA-rating r-${rule.rating}`}>{rule.rating}</span>
                    </strong>
                    <div>{rule.title}</div>
                    <small>{rule.summary}</small>
                  </div>
                  <div className="ac-actions">
                    <button type="button" className="ac-btn" onClick={() => toggle(rule)}>
                      {rule.isActive ? "Disable" : "Enable"}
                    </button>
                    <button type="button" className="ac-btn" onClick={() => open(rule)}>
                      Edit
                    </button>
                    <button type="button" className="ac-btn ac-btn-danger" onClick={() => remove(rule)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Leads({ token }) {
  const [page, setPage] = useState(1);
  const [mobile, setMobile] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminGetNumerologyReports({ page, mobile: query }, token);
    if (r.ok && r.success) {
      setData(r.data);
      setError("");
    } else setError(r.message || "Unable to load reports");
    setLoading(false);
  }, [page, query, token]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / data.limit));

  return (
    <div className="ac-wrap">
      <form
        className="mnA-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQuery(mobile.replace(/\D/g, ""));
        }}
      >
        <input className="ac-input" placeholder="Search by mobile number" value={mobile} onChange={(e) => setMobile(e.target.value)} />
        <button className="ac-btn ac-btn-primary" type="submit">
          Search
        </button>
      </form>
      <p className="mnA-hint">{data.total} analyses. Each row is a signed-in customer who checked a number.</p>
      {error && <div className="feedback-alert error">{error}</div>}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="mnA-table">
          {data.items.map((r) => (
            <div key={r.id} className="mnA-row">
              <div className="mnA-main">
                <strong>
                  {r.mobile} <span className={`mnA-rating v-${r.verdict}`}>{r.verdict.replace("_", " ").toLowerCase()}</span>
                </strong>
                <div>
                  Total {r.total} - Score {r.score}
                  {r.birthNumber ? ` - BN ${r.birthNumber} / DN ${r.destinyNumber}` : " - no DOB"}
                  {r.profession ? ` - ${r.profession}` : ""}
                </div>
                <small>
                  {r.user ? `${r.user.name} - ${r.user.email}${r.user.phone ? ` - ${r.user.phone}` : ""}` : "Unknown user"} -{" "}
                  {new Date(r.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </small>
              </div>
            </div>
          ))}
          {data.items.length === 0 && <p>No analyses yet.</p>}
        </div>
      )}
      <div className="ac-actions">
        <button className="ac-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>
        <span className="mnA-hint">
          Page {page} of {pages}
        </span>
        <button className="ac-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

const REQUEST_STATUSES = [
  ["new", "New"],
  ["contacted", "Contacted"],
  ["payment_received", "Payment received"],
  ["in_progress", "In progress"],
  ["number_shared", "Number shared"],
  ["delivered", "Delivered"],
  ["cancelled", "Cancelled"],
];

function Requests({ token }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminGetMobileNumberRequests({ page, status, search: query }, token);
    if (r.ok && r.success) {
      setData(r.data);
      setError("");
    } else setError(r.message || "Unable to load requests");
    setLoading(false);
  }, [page, status, query, token]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (req, payload) => {
    setSaving(true);
    const r = await adminUpdateMobileNumberRequest(req.id, payload, token);
    setSaving(false);
    if (r.ok && r.success) load();
    else setError(r.message || "Unable to update the request");
  };

  const pages = Math.max(1, Math.ceil(data.total / data.limit));
  const waLink = (r) => `https://wa.me/91${r.whatsapp}?text=${encodeURIComponent(`Hello ${r.fullName}, regarding your Mobile Number Customization request ${r.ref}.`)}`;

  return (
    <div className="ac-wrap">
      <form
        className="mnA-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <input className="ac-input" placeholder="Search ref, name, WhatsApp or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="ac-input" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
          <option value="">All statuses</option>
          {REQUEST_STATUSES.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <button className="ac-btn ac-btn-primary" type="submit">Search</button>
      </form>
      <p className="mnA-hint">{data.total} requests from the Mobile Number Customization form. The reference (MN-XXXXXX) is what the customer quotes when paying.</p>
      {error && <div className="feedback-alert error">{error}</div>}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="mnA-table">
          {data.items.map((r) => (
            <div key={r.id} className="mnA-row mnA-req">
              <div className="mnA-main">
                <strong>
                  {r.ref} - {r.fullName} <span className={`mnA-rating s-${r.status}`}>{r.status.replace("_", " ")}</span>
                </strong>
                <div>
                  WhatsApp {r.whatsapp} - {r.email}
                </div>
                <small>
                  {r.objective === "Other" ? `Other: ${r.objectiveOther}` : r.objective} - {new Date(r.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </small>
                {open === r.id && (
                  <div className="mnA-detail">
                    <div>Date of birth: {String(r.dateOfBirth).slice(0, 10).split("-").reverse().join("/")}</div>
                    <div>Time of birth: {r.timeOfBirth}</div>
                    <div>Place of birth: {r.placeOfBirth}</div>
                    <div>Current city / state: {r.currentCityState}</div>
                    <div>Consent given: {new Date(r.consentAt).toLocaleString("en-IN")}</div>
                    <textarea
                      className="ac-input"
                      rows={3}
                      placeholder="Internal notes"
                      value={notes[r.id] !== undefined ? notes[r.id] : r.adminNote || ""}
                      onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                    />
                    <button className="ac-btn" disabled={saving} onClick={() => update(r, { adminNote: notes[r.id] !== undefined ? notes[r.id] : r.adminNote || "" })}>
                      Save note
                    </button>
                  </div>
                )}
              </div>
              <div className="ac-actions">
                <select className="ac-input mnA-status" value={r.status} disabled={saving} onChange={(e) => update(r, { status: e.target.value })}>
                  {REQUEST_STATUSES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
                <a className="ac-btn" href={waLink(r)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                <button className="ac-btn" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? "Hide" : "Details"}</button>
              </div>
            </div>
          ))}
          {data.items.length === 0 && <p>No requests found.</p>}
        </div>
      )}
      <div className="ac-actions">
        <button className="ac-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
        <span className="mnA-hint">Page {page} of {pages}</span>
        <button className="ac-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
}

export default function AdminMobileNumerology({ token }) {
  const [tab, setTab] = useState("rules");
  return (
    <div className="panel-tab ac-wrap">
      <div className="ac-subtabs">
        <button className={`ac-subtab ${tab === "rules" ? "is-active" : ""}`} onClick={() => setTab("rules")}>
          Rules
        </button>
        <button className={`ac-subtab ${tab === "requests" ? "is-active" : ""}`} onClick={() => setTab("requests")}>
          Customization requests
        </button>
        <button className={`ac-subtab ${tab === "leads" ? "is-active" : ""}`} onClick={() => setTab("leads")}>
          Customer analyses
        </button>
      </div>
      {tab === "rules" ? <Rules token={token} /> : tab === "requests" ? <Requests token={token} /> : <Leads token={token} />}
    </div>
  );
}
